import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast, Collapse } from 'reactstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../types';
import { useVisibleToasts, useCloseToast } from '../../../../redux/slices/toasts/hooks';
import { ToastDisplayed, ToastType } from '../../../../redux/slices/toasts/state';
import { runToastAction } from '../../../../redux/slices/toasts/toastActions';
import { ToastLink } from './ToastLink';

/* eslint-disable css-modules/no-unused-class */
import styles from './Toasts.module.scss';
/* eslint-enable css-modules/no-unused-class */

const getIconForType = (type: ToastType) => {
  switch (type) {
    case ToastType.success:
      return <FontAwesomeIcon icon={faCheck} />;
    case ToastType.danger:
      return <FontAwesomeIcon icon={faCircleExclamation} />;
    case ToastType.warning:
      return <FontAwesomeIcon icon={faTriangleExclamation} />;
    case ToastType.info:
    default:
      return <FontAwesomeIcon icon={faCircleInfo} />;
  }
};

interface ToastItemProps {
  toast: ToastDisplayed;
}

const ToastItemComponent: React.FC<ToastItemProps> = ({ toast }) => {
  const [isShown, setIsShown] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const closeToast = useCloseToast();

  useEffect(() => {
    setIsShown(true);
  }, []);

  const handleClose = useCallback(() => {
    closeToast(toast.id);
  }, [closeToast, toast.id]);

  const actionId = toast.action?.actionId;
  const handleAction = useCallback(() => {
    if (actionId !== undefined) {
      runToastAction(actionId);
    }
    closeToast(toast.id);
  }, [actionId, closeToast, toast.id]);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  // Auto-hide toast after 5 seconds if enabled and not hovering
  useEffect(() => {
    if (isHovering || toast.autoHide !== true) {
      return;
    }
    const timeoutId = setTimeout(() => {
      handleClose();
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [isHovering, toast.autoHide, handleClose]);

  const isOpen = isShown && !toast.isDismissed;

  return (
    <Collapse isOpen={isOpen} className="pt-2">
      <Toast
        className={classNames('d-block', styles.toast, styles[`toast-${toast.type}`])}
        isOpen={isOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={classNames('toast-body d-flex align-items-start gap-2', styles.toastBody)}>
          <div className={classNames(styles.toastIcon, styles[`icon-${toast.type}`])}>{getIconForType(toast.type)}</div>
          <div className="flex-grow-1">
            {toast.title !== undefined && (
              <div className={classNames('fw-semibold', styles.toastTitle)}>{toast.title}</div>
            )}
            <div className={styles.toastDescription}>{toast.description}</div>
            {toast.linkUrl !== undefined && <ToastLink url={toast.linkUrl} />}
            {toast.action !== undefined && (
              <button type="button" className={styles.toastAction} onClick={handleAction}>
                {toast.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            className={classNames('btn btn-link p-0', styles.closeButton)}
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </Toast>
    </Collapse>
  );
};

const ToastItem = TypedMemo(ToastItemComponent);

const ToastsComponent: React.FC = () => {
  const toasts = useVisibleToasts();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className={styles.container}>
      {toasts.map((toast) => (
        <ToastItem toast={toast} key={toast.id} />
      ))}
    </div>,
    document.body
  );
};

export const Toasts = TypedMemo(ToastsComponent);
